import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

import css from "./OrderDetailsLeft.module.css";
import {
  formatDateToString,
  formatDateToStringWithoutPadding,
  formatDateWithoutLocalDate,
  getNormalizeDate,
} from "../../../../helpers";
import SelectPaymentStatusModal from "../SelectPaymentStatusModal/SelectPaymentStatusModal";
import { HistoryOrderModal } from "../HistoryOrderModal/HistoryOrderModal";
import { DishReplacementsModal } from "../DishReplacementsModal/DishReplacementsModal";
import { CreatePaymentModal } from "../CreatePaymentModal/CreatePaymentModal";
import { PaymentHistoryRow } from "../PaymentHistoryRow/PaymentHistoryRow";
import { ordersService } from "../../../../services/orders-services";
import { ordersActions } from "../../../../store/slices/orders-slices";
import dayjs from "dayjs";

const OrderDetailsLeft = ({ order, totalCost }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isShowHistoryModal, setIsShowHistoryModal] = useState(false);
  const [isShowDishReplacementsModal, setIsShowDishReplacementsModal] =
    useState(false);
  const [isShowCreatePaymentModal, setIsShowCreatePaymentModal] =
    useState(false);
  const [localDebt, setLocalDebt] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [changelogLoading, setChangelogLoading] = useState(false);

  useEffect(() => {
    if (order?.debt !== undefined) {
      setLocalDebt(Number(order.debt) > 0);
    }
  }, [order?.debt]);

  useEffect(() => {
    if (!order?.id) return;
    setChangelogLoading(true);
    ordersService.getChangelogById(order.id)
      .then(setChangelog)
      .catch(() => {})
      .finally(() => setChangelogLoading(false));
  }, [order?.id]);

  if (!order) return null;

  const {
    id,
    client,
    creationDate,
    daysOrdered,
    pack,
    paymentAmount,
    paymentMethod,
    paymentStatus,
    discountAmount,
    bonusDiscount,
    points,
  } = order;

  const contact = client?.phoneNumber;
  const deliveryInterval = client?.deleveryInterval;
  const assignedCourier = client?.courier;

  const formatted = getNormalizeDate(creationDate);

  const handleDebt = async (e) => {
    const isDebtNow = e.target.checked;

    // оптимістично оновлюємо локальний стан
    setLocalDebt(isDebtNow);

    try {
      if (isDebtNow) {
        // якщо встановили борг
        await ordersService.updateOrderById(id, { isDebt: true });
        toast.info(t("toast-debt-set"));
      } else {
        // якщо зняли борг — запускаємо логіку повернення
        await ordersService.updateOrderById(id, { isDebt: false });
        toast.success(t("toast-debt-cleared"));
      }

      // Small delay to ensure database transaction is fully committed
      await new Promise((resolve) => setTimeout(resolve, 100));

      dispatch(ordersActions.changeTrigger());
    } catch (error) {
      // відкат локального стану у разі помилки
      setLocalDebt(!isDebtNow);
      console.error(t("modal-toast-fetch-error"), error);
      toast.error(t("modal-toast-fetch-error"));
    }
  };

  const showHistoryByOrder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowHistoryModal(true);
  };

  const showDishReplacements = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowDishReplacementsModal(true);
  };

  const showCreatePayment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowCreatePaymentModal(true);
  };

  return (
    <div className={css.wrap}>
      <div className={css.general}>
        <div className={css.title}>{t("general-info")}</div>

        <div className={css.header}>
          <div className={css.created}>{t("created")}</div>
          <div className={css.dietType}>{t("diet-type")}</div>
          <div className={css.days}>{t("days")}</div>
          <div className={css.phone}>{t("phone")}</div>
          <div className={css.contact}>{t("contact")}</div>
          <div className={css.name}>{t("modal-name-input-new-category")}</div>
          <div className={css.deliveryInterval}>{t("delivery-interval")}</div>
          <div className={css.courier}>{t("courier")}</div>
        </div>

        <div className={css.headerInfo}>
          {/* <div className={css.created}>{dayjs(creationDate).format("DD.MM.YYYY")}</div> */}
          <div className={css.created}>{formatted}</div>
          <div className={css.dietType}>{pack?.name?.en || "-"}</div>
          <div className={css.days}>{daysOrdered}</div>
          <div className={css.phone}>{contact}</div>
          <div className={css.contact}>
            {client?.communicationMethod || "-"}
            {client?.communicationDetails && (
              <div style={{ fontSize: "0.8em", color: "#666" }}>
                {client.communicationDetails}
              </div>
            )}
          </div>
          <div className={css.name}>{client?.clientName || "-"}</div>
          <div className={css.deliveryInterval}>{deliveryInterval}</div>
          <div className={css.courier}>{assignedCourier || "-"}</div>
        </div>
      </div>

      <div className={css.history}>
        <div className={css.titleRow}>
          <div className={css.title}>{t("order-history")}</div>
          <button
            className={css.createPaymentButton}
            onClick={showCreatePayment}
          >
            + {t("create-payment")}
          </button>
        </div>

        <div className={css.header}>
          <div className={css.createdAt}>{t("created")}</div>
          <div className={css.paymentNumber}>{t("payment-number")}</div>
          <div className={css.paymentNumber}>{t("Price per day")}</div>
          <div className={css.amount}>{t("Amount")}</div>
          <div className={css.points}>{t("points")}</div>
          <div className={css.paymentMethod}>{t("payment-method")}</div>
          <div className={css.paymentStatus}>{t("order-status")}</div>
          <div className={css.debt}>{t("debt")}</div>
        </div>

        <div className={css.historyHeaderInfo} onClick={showHistoryByOrder}>
          <div className={css.createdAt}>{formatted}</div>
          <div className={css.paymentNumber}>-</div>
          <div className={css.paymentNumber}>
            {daysOrdered ? (paymentAmount / daysOrdered).toFixed(2) : "-"}
          </div>
          <div className={css.amount}>
            {paymentStatus === "Created" || paymentStatus === "Completed"
              ? paymentAmount
              : "-"}
          </div>
          <div className={css.points}>{points}</div>
          <div className={css.paymentMethod}>{paymentMethod}</div>
          <div className={css.paymentStatus}>
            <SelectPaymentStatusModal
              key={`payment-status-${id}`}
              paymentStatus={paymentStatus}
              id={id}
            />
          </div>
          <div className={css.debt}>
            <input
              type="checkbox"
              onClick={(e) => e.stopPropagation()}
              onChange={handleDebt}
              checked={localDebt}
              disabled={
                paymentStatus !== "Created" && paymentStatus !== "Completed"
              }
            />
          </div>
        </div>
      </div>

      {/* Payment History Block */}
      {order.orderPayments && order.orderPayments.length > 0 && (
        <div className={css.paymentHistory}>
          <div className={css.title}>{t("payment-history")}</div>
          <div className={css.header}>
            <div className={css.createdAt}>{t("created")}</div>
            <div className={css.amount}>{t("Amount")}</div>
            <div className={css.paymentMethod}>{t("payment-method")}</div>
            <div className={css.paymentStatus}>{t("payment-status")}</div>
          </div>
          {order.orderPayments.map((payment) => (
            <PaymentHistoryRow
              key={payment.id}
              payment={payment}
              orderId={id}
            />
          ))}
        </div>
      )}

      {/* Order Amount Block */}
      <div className={css.orderAmount}>
        <div className={css.title}>{t("order-amount")}</div>
        <div className={css.header}>
          <div className={css.paidAmount}>{t("paid")}</div>
          <div className={css.unpaidAmount}>{paymentStatus === "Completed" && order.unpaid > 0 ? t("replacement-charge") : t("unpaid")}</div>
          <div className={css.replacementCharges}>
            {t("replacement-charges")}
          </div>
        </div>
        <div className={css.historyHeaderInfo} onClick={showDishReplacements}>
          <div className={css.paidAmount}>{order.paid || "-"}</div>
          <div className={css.unpaidAmount}>{order.unpaid || "-"}</div>
          <div className={css.replacementCharges}>
            {order.replacementCharges || "-"}
          </div>
        </div>
      </div>

      {/* Changelog Block */}
      <div className={css.changelog}>
        <div className={css.title}>{t("changelog")}</div>
        {changelogLoading ? (
          <div className={css.changelogSpinner}><div className={css.spinner} /></div>
        ) : changelog.length === 0 ? (
          <div className={css.changelogEmpty}>{t("no-changelog")}</div>
        ) : (
          <>
            <div className={css.header}>
              <div className={css.changelogDate}>{t("date")}</div>
              <div className={css.changelogDesc}>{t("description")}</div>
              <div className={css.changelogUser}>{t("user")}</div>
            </div>
            {changelog.map((entry) => (
              <div key={entry.id} className={css.historyHeaderInfo}>
                <div className={css.changelogDate}>
                  {dayjs(entry.createdAt).format("DD.MM.YYYY HH:mm")}
                </div>
                <div className={css.changelogDesc}>{entry.description}</div>
                <div className={css.changelogUser}>
                  {entry.user?.role === 'Admin' || entry.user?.role === 'Manager'
                    ? t("admin")
                    : entry.user?.clientName || entry.user?.phoneNumber || t("user")}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Additional Info Block */}
      <div className={css.additionalInfo}>
        <div className={css.title}>{t("additional-info")}</div>
        <div className={css.header}>
          <div className={css.promoCode}>{t("promo-code")}</div>
          <div className={css.discountAmount}>{t("discount-amount")}</div>
          <div className={css.bonusDiscount}>{t("bonus-discount")}</div>
        </div>
        <div className={css.historyHeaderInfo}>
          <div className={css.promoCode}>
            {order.claimedPromoCodes?.[0]?.promoCode?.promoCode ?? "-"}
          </div>
          <div className={css.discountAmount}>
            {discountAmount !== undefined ? discountAmount : "-"}
          </div>
          <div className={css.bonusDiscount}>
            {bonusDiscount !== undefined ? bonusDiscount : "-"}
          </div>
        </div>
      </div>

      {isShowHistoryModal && (
        <HistoryOrderModal
          setIsShowHistoryModal={setIsShowHistoryModal}
          id={id}
        />
      )}

      {isShowDishReplacementsModal && (
        <DishReplacementsModal
          setIsShowDishReplacementsModal={setIsShowDishReplacementsModal}
          dishReplacements={order.dishReplacements}
        />
      )}

      {isShowCreatePaymentModal && (
        <CreatePaymentModal
          setIsShowCreatePaymentModal={setIsShowCreatePaymentModal}
          orderId={id}
        />
      )}
    </div>
  );
};

export default React.memo(OrderDetailsLeft);
